import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Drawer from '@mui/material/Drawer'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import CssBaseline from '@mui/material/CssBaseline'
import { Container, Typography, useMediaQuery, useTheme, Button } from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { changeCurrentLanguage, setPtRuns } from '../../store/actions'
import { ReactComponent as PayTraLogo } from '../../assets/logos/Pay_transparency.svg'
import { ReactComponent as PayTraLogoDark } from '../../assets/logos/PT_dark.svg'
import { ReactComponent as LanguageLogo } from '../../assets/walkthrough/language.svg'
import { updateProfile } from '../../store/actions/user/thunk'
import PtNavigation from '../fs-navigation/pt-navigation'
import FsAppsMenu from './AppsMenu'
import FsAccountMenu from './AccountMenu'

const PtDrawer = () => {
  const { token, account, currentLanguage, currentTheme } = useAppSelector(
    (state) => state.currentUser,
  )
  const { ptRuns } = useAppSelector((state) => state.currentPt)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const theme = useTheme()
  const largerThanMid = useMediaQuery(theme.breakpoints.up('sm'))
  const [isMenuHovered, setIsMenuHovered] = useState<boolean>(false)
  const [isShowText, setIsShowText] = useState<boolean>(false)

  const switchLanguage = (l: 'de' | 'en') => {
    i18n.changeLanguage(l)
    document.documentElement.setAttribute('lang', l)
    dispatch(changeCurrentLanguage(l))
  }

  useEffect(() => {
    i18n.changeLanguage(currentLanguage)
    document.documentElement.setAttribute('lang', currentLanguage)
  }, [])

  const isPtApp = account?.profile.appAccess?.includes('pt')

  useEffect(() => {
    if (isPtApp) {
      dispatch(setPtRuns())
    } else {
      navigate('/ines/home')
    }
  }, [isPtApp])

  const noAnalysisYet = ptRuns && ptRuns.length === 0

  useEffect(() => {
    const path = location.pathname
    if (
      !path.startsWith('/pt/upload-files') &&
      !path.startsWith('/pt/data-upload-steps') &&
      !path.startsWith('/pt/home') &&
      !path.startsWith('/pt/admin/import') &&
      noAnalysisYet
    ) {
      navigate('/pt/upload-files')
    }
  }, [location, ptRuns])

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: currentTheme === 'light' ? theme.palette.grey[50] : '',
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1600,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          margin: 0.5,
          width: 'calc(100% - 8px)',
        }}
        elevation={3}
      >
        <Toolbar
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1600,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {token && <FsAppsMenu />}
            <div
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => navigate('/pt/home')}
            >
              {currentTheme === 'dark' ? (
                <PayTraLogoDark width="auto" height="25px" />
              ) : (
                <PayTraLogo width="auto" height="25px" />
              )}
            </div>
          </div>

          {token && <FsAccountMenu />}
        </Toolbar>
      </AppBar>

      <Drawer
        key={`key_${location.pathname}`}
        variant="permanent"
        sx={{
          [`& .MuiDrawer-paper`]: {
            width: isMenuHovered ? 300 : 62,
            position: 'fixed',
            zIndex: 1200,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            overflowX: 'hidden',
            margin: 0.5,
            px: 1,
            height: 'fit-content',
            maxHeight: 'calc(100vh - 84px)',
            top: 72,
            transition: 'width 0.25s',
            boxShadow: `0px 2px 4px -1px ${theme.palette.action.hover}, 0px 2px 2px 0px ${theme.palette.action.hover}, 0px 1px 10px 0px rgba(0, 0, 0, 0.12)`,
          },
        }}
        onMouseEnter={() => {
          setIsMenuHovered(true)
          setTimeout(() => {
            setIsShowText(true)
          }, 250)
        }}
        onMouseLeave={() => {
          setTimeout(() => {
            setIsShowText(false)
            setIsMenuHovered(false)
          }, 250)
        }}
      >
        <PtNavigation isHovered={isShowText} />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 2,
          pb: 3,
          pl: 8.5,
          height: '100vh',
          width: 'calc(100% - 128px)',
          overflowY: 'hidden',
        }}
      >
        <Toolbar />
        <Container
          maxWidth="xl"
          sx={{ overflowY: 'hidden', height: 'calc(100vh - 89px)', pt: 1.5 }}
        >
          {largerThanMid ? (
            <Outlet />
          ) : (
            <Box>
              <Typography variant="h2" textAlign="center">
                {t('no.small.screen')}
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      {!account?.profile || account?.profile.isFirstTime ? (
        <Box
          height="100%"
          width="100%"
          position="absolute"
          top={0}
          left={0}
          sx={{ backgroundColor: '#00000080', zIndex: 1300 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            py={4}
            width="400px"
            sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <LanguageLogo color={theme.palette.primary.main} />
            <Typography mt={2} mx={4}>
              Wählen Sie Ihre gewünschte Sprache.
            </Typography>
            <Typography mx={4}>Choose your prefered language.</Typography>
            <Button
              variant="contained"
              sx={{
                mt: 2,
              }}
              onClick={() => {
                dispatch(
                  updateProfile({
                    profile: { preferredLanguage: 'de', isFirstTime: false },
                  }),
                )
                switchLanguage('de')
              }}
            >
              Weiter auf Deutsch
            </Button>
            <Button
              variant="contained"
              sx={{
                mt: 2,
              }}
              onClick={() => {
                dispatch(
                  updateProfile({
                    profile: { preferredLanguage: 'en', isFirstTime: false },
                  }),
                )
                switchLanguage('en')
              }}
            >
              Continue in English
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export default PtDrawer
